import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Select, Space, message } from 'antd';

/**
 * 队伍品牌文字：仅四段文本覆盖，禁止写全局 ui_settings。
 */
export function buildOrgBrandingTab(context) {
  const { shouldShowPage, apiRequest } = context;
  if (!shouldShowPage('队伍品牌文字设置')) return null;
  if (typeof apiRequest !== 'function') return null;

  return {
    key: 'org_ui_branding',
    label: '队伍品牌',
    children: <OrgBrandingPanel apiRequest={apiRequest} />
  };
}

function emptyForm() {
  return {
    brand_text_pc: '',
    brand_text_mobile: '',
    title_text_pc: '',
    title_text_mobile: ''
  };
}

function rowToForm(row) {
  return {
    brand_text_pc: row?.brand_text_pc || '',
    brand_text_mobile: row?.brand_text_mobile || '',
    title_text_pc: row?.title_text_pc || '',
    title_text_mobile: row?.title_text_mobile || ''
  };
}

function OrgBrandingPanel({ apiRequest }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [homeOrg, setHomeOrg] = useState(null);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadManageable = useCallback(async () => {
    const data = await apiRequest('/api/admin/org-ui-branding/manageable-orgs');
    if (!data?.success) {
      throw new Error(data?.error || '无法获取可管理组织');
    }
    const list = Array.isArray(data.orgs) ? data.orgs.filter(Boolean) : [];
    setOrgs(list);
    setHomeOrg(data.homeOrg || null);
    setIsSystemAdmin(!!data.isSystemAdmin);
    const preferred = data.homeOrg || list[0] || null;
    setSelectedOrg((prev) => (prev && list.includes(prev) ? prev : preferred));
    return preferred;
  }, [apiRequest]);

  const loadBranding = useCallback(async (orgName) => {
    if (!orgName) {
      setForm(emptyForm());
      setUpdatedAt(null);
      return;
    }
    const data = await apiRequest(`/api/admin/org-ui-branding?org_name=${encodeURIComponent(orgName)}`);
    if (!data?.success) {
      throw new Error(data?.error || '读取覆盖失败');
    }
    setForm(rowToForm(data.branding));
    setUpdatedAt(data.branding?.updated_at || null);
  }, [apiRequest]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const preferred = await loadManageable();
        if (cancelled) return;
        if (preferred) await loadBranding(preferred);
      } catch (e) {
        if (!cancelled) message.error(e.message || '加载队伍品牌失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadManageable, loadBranding]);

  const onOrgChange = async (orgName) => {
    setSelectedOrg(orgName);
    setLoading(true);
    try {
      await loadBranding(orgName);
    } catch (e) {
      message.error(e.message || '切换组织失败');
    } finally {
      setLoading(false);
    }
  };

  const patchField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selectedOrg) {
      message.warning('请先选择组织，或在帐号中设置所属组织');
      return;
    }
    setSaving(true);
    try {
      const data = await apiRequest('/api/admin/org-ui-branding', {
        method: 'PUT',
        body: JSON.stringify({
          org_name: selectedOrg,
          brand_text_pc: form.brand_text_pc,
          brand_text_mobile: form.brand_text_mobile,
          title_text_pc: form.title_text_pc,
          title_text_mobile: form.title_text_mobile
        })
      });
      if (!data?.success) throw new Error(data?.error || '保存失败');
      setForm(rowToForm(data.branding));
      setUpdatedAt(data.branding?.updated_at || null);
      if (data.cleared) {
        message.success('已清除覆盖，回落系统默认');
      } else if (!homeOrg) {
        message.warning('已保存到该组织；但本帐号所属组织未设置，返回首页仍看系统默认。请先设置所属组织后再预览。');
      } else if (selectedOrg && selectedOrg !== homeOrg) {
        message.warning(`已保存到「${selectedOrg}」；你的首页仍按所属组织「${homeOrg}」显示。`);
      } else {
        message.success('队伍品牌文字已保存；硬刷新首页即可看到覆盖');
      }
    } catch (e) {
      message.error(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selectedOrg) {
      message.warning('请先选择组织');
      return;
    }
    setSaving(true);
    try {
      const data = await apiRequest('/api/admin/org-ui-branding', {
        method: 'PUT',
        body: JSON.stringify({
          org_name: selectedOrg,
          brand_text_pc: '',
          brand_text_mobile: '',
          title_text_pc: '',
          title_text_mobile: ''
        })
      });
      if (!data?.success) throw new Error(data?.error || '清除失败');
      setForm(emptyForm());
      setUpdatedAt(data.branding?.updated_at || null);
      message.success('已清除本组织覆盖，显示将回落系统默认');
    } catch (e) {
      message.error(e.message || '清除失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3>队伍品牌</h3>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="只改本组织四段文字覆盖，不会改动全站默认样式或字号颜色。"
        description="空字段 = 回落系统默认（首页设置 / 标题栏设置里的文案）。访客与未登录用户始终看系统默认。首页/顶栏只按「登录帐号的所属组织」合并覆盖，不会按你正在代管编辑的组织自动切换。"
      />

      {!homeOrg ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="当前帐号所属组织未设置：返回首页仍显示系统默认文案"
          description="代管保存只会写入所选组织的覆盖表，不会套到无所属组织的帐号上。请到「队员管理」编辑本帐号，把所属组织设为要预览的队伍（例如「龙泉户外救援队」），再硬刷新首页即可看到覆盖。"
        />
      ) : selectedOrg && selectedOrg !== homeOrg ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`正在代管编辑「${selectedOrg}」，你的首页仍按所属组织「${homeOrg}」显示`}
          description="覆盖按组织隔离。要在自己的首页预览本次文案，请把所属组织改成正在编辑的组织，或换用该组织帐号登录查看。"
        />
      ) : null}

      <div style={{ marginBottom: 12, color: '#666' }}>
        当前所属组织：
        <strong style={{ marginLeft: 6 }}>{homeOrg || '（未设置）'}</strong>
        {isSystemAdmin ? <span style={{ marginLeft: 12 }}>系统管理员可代管任意组织</span> : null}
      </div>

      <div style={{ marginBottom: 16, maxWidth: 420 }}>
        <div style={{ marginBottom: 6 }}>编辑组织</div>
        <Select
          style={{ width: '100%' }}
          placeholder={orgs.length ? '选择组织' : '无可管理组织'}
          value={selectedOrg || undefined}
          onChange={onOrgChange}
          options={orgs.map((name) => ({ value: name, label: name }))}
          disabled={loading || orgs.length === 0}
          showSearch
          optionFilterProp="label"
        />
        {!homeOrg && orgs.length === 0 ? (
          <p style={{ color: '#b45309', marginTop: 8 }}>
            帐号无所属组织且未勾选品牌代管时，无法编辑。请联系系统管理员。
          </p>
        ) : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 900 }}>
        <div>
          <h4>PC 品牌文字</h4>
          <Input
            value={form.brand_text_pc}
            onChange={(e) => patchField('brand_text_pc', e.target.value)}
            placeholder="空 = 系统默认"
            maxLength={40}
            disabled={loading || !selectedOrg}
          />
        </div>
        <div>
          <h4>手机品牌文字</h4>
          <Input
            value={form.brand_text_mobile}
            onChange={(e) => patchField('brand_text_mobile', e.target.value)}
            placeholder="空 = 系统默认"
            maxLength={40}
            disabled={loading || !selectedOrg}
          />
        </div>
        <div>
          <h4>PC 标题文本</h4>
          <Input
            value={form.title_text_pc}
            onChange={(e) => patchField('title_text_pc', e.target.value)}
            placeholder="空 = 系统默认"
            maxLength={80}
            disabled={loading || !selectedOrg}
          />
        </div>
        <div>
          <h4>手机标题文本</h4>
          <Input
            value={form.title_text_mobile}
            onChange={(e) => patchField('title_text_mobile', e.target.value)}
            placeholder="空 = 系统默认"
            maxLength={80}
            disabled={loading || !selectedOrg}
          />
        </div>
      </div>

      <Space style={{ marginTop: 20 }}>
        <Button type="primary" onClick={handleSave} loading={saving} disabled={!selectedOrg || loading}>
          保存覆盖
        </Button>
        <Button onClick={handleClear} loading={saving} disabled={!selectedOrg || loading}>
          清除覆盖
        </Button>
      </Space>
      {updatedAt ? (
        <div style={{ marginTop: 10, color: '#999', fontSize: 12 }}>最近更新：{String(updatedAt)}</div>
      ) : null}
    </div>
  );
}
